/**
 * BPAS Client Portal API Helper
 * Handles communication with the Google Apps Script Gateway
 */

const GATEWAY_URL = import.meta.env.VITE_BPAS_CLIENT_PORTAL_GATEWAY_URL;

/**
 * Enhanced fetch with timeout
 */
const fetchWithTimeout = async (url, options, timeout = 25000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    throw error;
  }
};

/**
 * Base API call handler
 */
export const apiCall = async (action, payload = {}, adminToken = null, customTimeout = null) => {
  if (!GATEWAY_URL) {
    throw new Error('Gateway URL not configured.');
  }

  const body = {
    action,
    payload
  };

  if (adminToken) {
    body.admin_session_token = adminToken;
  }

  try {
    const response = await fetchWithTimeout(
      GATEWAY_URL,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8'
        },
        body: JSON.stringify(body),
      },
      customTimeout || 25000
    );

    if (!response.ok) throw new Error(`Server Error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API Error (${action}):`, error);
    throw error;
  }
};

const normalizeLeads = (leads = []) => {
  return leads.map(lead => ({
    ...lead,
    id: lead.lead_id,
    name: lead.visitor_name,
    email: lead.visitor_email,
    phone: lead.visitor_phone,
    created_at: lead.submitted_at,
    source: lead.utm_source,
    campaign: lead.utm_campaign
  }));
};

export const authApi = {
  verifyLogin: async (email, code) => {
    const response = await apiCall('verifyClientLogin', { email, code });
    if (response.success && response.client) {
      response.client = {
        ...response.client,
        session_token: response.session_token,
        expires_at: response.expires_at
      };
    }
    return response;
  },
  logout: (token) => apiCall('logoutClientPortal', { session_token: token }),
};

export const adminApi = {
  verifyLogin: async (email, code, remember) => {
    const response = await apiCall('verifyAdminLogin', { email, code, remember });
    return response;
  },
  logout: (token) => apiCall('logoutAdminPortal', {}, token),
  getStats: (token) => apiCall('getDashboardStats', {}, token),
  getClients: (token) => apiCall('getClients', {}, token),
  getClient: (token, key) => apiCall('getClient', { client_key: key }, token),
  addClient: (token, data) => apiCall('addClient', data, token),
  updateClient: (token, data) => apiCall('updateClient', data, token),
  getRecentLeads: (token) => apiCall('getRecentLeads', {}, token),
  getLogs: (token) => apiCall('getLogs', {}, token),
  sendTestLead: (token, key) => apiCall('sendTestLead', { client_key: key }, token, 90000),
  createSheet: (token, key, email) => apiCall('createClientLeadSheet', { 
    client_key: key, 
    share_with_client: true, 
    share_email: email 
  }, token),
  healthCheck: (token) => apiCall('healthCheck', {}, token),
};

export const leadsApi = {
  getLeads: async (clientKey, email, token) => {
    const response = await apiCall('getClientPortalLeads', { 
      client_key: clientKey, 
      email: email, 
      session_token: token 
    });
    if (response.success && response.leads) {
      response.leads = normalizeLeads(response.leads);
    }
    return response;
  },
};